import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search, Menu, HelpCircle, HomeIcon, Users, ClipboardList, Eye, Shield, MessageSquare, MapPin, ChevronLeft, ChevronRight, TrendingDown } from 'lucide-react';
type ChartPoint = {
  month: string;
  score: number;
  event?: string;
};
const CHART_DATA: ChartPoint[] = [{
  month: 'Jul',
  score: 68,
  event: 'CEO Tony Stark announced retirement'
}, {
  month: 'Aug',
  score: 62
}, {
  month: 'Sep',
  score: 53,
  event: 'Major cybersecurity breach reported'
}, {
  month: 'Oct',
  score: 49
}, {
  month: 'Nov',
  score: 45,
  event: 'DBT increased 180% (12 → 34 days)'
}, {
  month: 'Dec',
  score: 43
}];
const VB_W = 420;
const VB_H = 200;
const SCORE_MIN = 0;
const SCORE_MAX = 100;
const PAD_LEFT = 36;
const PAD_RIGHT = 20;
const PAD_TOP = 12;
const PAD_BOTTOM = 22;
function toXY(i: number) {
  const x = PAD_LEFT + i / (CHART_DATA.length - 1) * (VB_W - PAD_LEFT - PAD_RIGHT);
  const y = PAD_TOP + (SCORE_MAX - CHART_DATA[i].score) / (SCORE_MAX - SCORE_MIN) * (VB_H - PAD_TOP - PAD_BOTTOM);
  return {
    x,
    y
  };
}
function scoreLine() {
  return CHART_DATA.map((_, i) => {
    const {
      x,
      y
    } = toXY(i);
    return i === 0 ? `M${x},${y}` : `L${x},${y}`;
  }).join(' ');
}
function scoreToY(s: number) {
  return PAD_TOP + (SCORE_MAX - s) / (SCORE_MAX - SCORE_MIN) * (VB_H - PAD_TOP - PAD_BOTTOM);
}
function getScoreColor(s: number) {
  if (s > 50) return {
    text: 'text-amber-600',
    bg: 'bg-amber-50',
    stroke: '#f59e0b',
    label: 'Moderate Risk',
    labelText: 'text-amber-700',
    labelBg: 'bg-amber-50',
    labelBorder: 'border-amber-200'
  };
  return {
    text: 'text-red-600',
    bg: 'bg-red-50',
    stroke: '#ef4444',
    label: 'High Risk',
    labelText: 'text-red-700',
    labelBg: 'bg-red-50',
    labelBorder: 'border-red-200'
  };
}
function dotColor(score: number): string {
  if (score >= 70) return '#22c55e';
  if (score > 50) return '#f59e0b';
  return '#ef4444';
}
const NOTIFICATIONS = [{
  event: 'CEO Tony Stark announced retirement effective immediately — leadership transition underway',
  date: '7/14/2025',
  impact: 'red',
  ack: '--'
}, {
  event: 'Major cybersecurity breach reported — customer data compromised, regulatory investigation launched',
  date: '9/22/2025',
  impact: 'red',
  ack: '--'
}, {
  event: 'DBT increased 180% in the past quarter (from 12 days to 34 days)',
  date: '11/3/2025',
  impact: 'red',
  ack: 'Dave Davidson'
}];
// Indices of data points that have events
const EVENT_INDICES = CHART_DATA.map((d, i) => d.event ? i : -1).filter(i => i >= 0);
function ImpactIcon({
  type
}: {
  type: 'red' | 'green';
}) {
  const color = type === 'red' ? 'bg-red-500' : 'bg-green-500';
  return <div className="flex items-end gap-0.5 h-4" data-id="element-717">
      <div className={`w-1 h-2 ${color} rounded-sm opacity-60`} data-id="element-718" />
      <div className={`w-1 h-3 ${color} rounded-sm opacity-80`} data-id="element-719" />
      <div className={`w-1 h-4 ${color} rounded-sm`} data-id="element-720" />
    </div>;
}
export function DecisionReport({
  onComplete
}: {
  onComplete?: () => void;
}) {
  const [phase, setPhase] = useState(1);
  const [score, setScore] = useState(68);
  const [scoutText, setScoutText] = useState('');
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const colors = getScoreColor(score);
  useEffect(() => {
    // Score animation — starts at 68 (amber) and declines to 43 (red)
    let current = 68;
    const end = 43;
    const duration = 2500;
    const incrementTime = 30;
    const steps = duration / incrementTime;
    const decrement = (68 - end) / steps;
    setScore(68);
    const timer = setInterval(() => {
      current -= decrement;
      if (current <= end) {
        setScore(end);
        clearInterval(timer);
      } else {
        setScore(Math.round(current));
      }
    }, incrementTime);
    // Phases
    const timeouts = [
    // Auto-hover event points one by one
    setTimeout(() => setHoveredPoint(EVENT_INDICES[0]), 2000), setTimeout(() => setHoveredPoint(EVENT_INDICES[1]), 3500), setTimeout(() => setHoveredPoint(EVENT_INDICES[2]), 5000), setTimeout(() => setHoveredPoint(null), 6500), setTimeout(() => {
      setPhase(3);
      setPhase(4);
    }, 7500), setTimeout(() => setPhase(5), 10000) // Scout bot appears
    ];
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 19000);
    return () => {
      clearInterval(timer);
      timeouts.forEach(clearTimeout);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);
  useEffect(() => {
    if (phase === 5) {
      const fullText = 'Stark Industries shows significant risk signals. CEO departure in July triggered instability, followed by a major cybersecurity breach in September and a 180% spike in DBT by November. Score declined from 68 to 43 in 6 months. Recommend reducing credit limit to $25,000 and increasing monitoring frequency to weekly.';
      let i = 0;
      const typingTimer = setInterval(() => {
        if (i <= fullText.length) {
          setScoutText(fullText.slice(0, i));
          i++;
        } else {
          clearInterval(typingTimer);
        }
      }, 20);
      return () => clearInterval(typingTimer);
    }
  }, [phase]);
  const activeTab = phase >= 3 ? 'Notifications' : 'Overview';
  return <div className="w-full h-full bg-white rounded-xl overflow-hidden flex flex-col text-sm border border-gray-200/50" data-id="element-721">
      {/* Product Header */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-200 bg-white shrink-0" data-id="element-722">
        <Menu className="w-4 h-4 text-gray-500" data-id="element-723" />
        <img src="https://cdn.magicpatterns.com/uploads/a5S8ZQgbN9jzaBrUDmr99x/Logo_CreditPulse_Black.png" alt="Credit Pulse" className="h-5 object-contain" data-id="element-724" />
        <div className="flex-1 mx-3 relative" data-id="element-725">
          <input type="text" placeholder="Search Companies" className="w-full pl-3 pr-8 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none" data-id="element-726" />
          <Search className="w-3 h-3 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2" data-id="element-727" />
        </div>
        <HelpCircle className="w-4 h-4 text-gray-400" data-id="element-728" />
        <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[8px] font-bold text-white" data-id="element-729">
          SU
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden" data-id="element-730">
        {/* Left Sidebar */}
        <div className="w-10 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-3.5 shrink-0" data-id="element-731">
          <HomeIcon className="w-4 h-4 text-gray-400" data-id="element-732" />
          <Users className="w-4 h-4 text-gray-400" data-id="element-733" />
          <ClipboardList className="w-4 h-4 text-gray-400" data-id="element-734" />
          <div className="w-7 h-7 rounded-md bg-[#6C5CE7]/10 flex items-center justify-center" data-id="element-735">
            <Eye className="w-4 h-4 text-[#6C5CE7]" data-id="element-736" />
          </div>
          <Shield className="w-4 h-4 text-gray-400" data-id="element-737" />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden" data-id="element-738">
          {/* Company Header */}
          <div className="px-5 py-2 border-b border-gray-200 shrink-0" data-id="element-739">
            <div className="flex items-center mb-1.5" data-id="element-740">
              <div className="flex-1 min-w-0" data-id="element-741">
                <h2 className="text-base font-bold text-gray-900 leading-tight" data-id="element-742">
                  STARK INDUSTRIES
                </h2>
                <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-743">
                  Credit Manager: Dave Davidson
                </p>
              </div>
              <div className="flex flex-col items-center shrink-0 mx-auto" data-id="element-744">
                <div className="flex items-center gap-1.5 mb-0.5" data-id="element-745">
                  <span className="text-[10px] font-medium text-gray-500" data-id="element-746">
                    Pulse Score
                  </span>
                  <span className={`text-sm font-bold ${colors.text} ${colors.bg} px-1.5 py-0.5 rounded transition-colors duration-300`} data-id="element-747">
                    {score}
                  </span>
                </div>
                <div className="w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden flex relative" data-id="element-748">
                  <div className="h-full bg-red-400 w-1/3" data-id="element-749" />
                  <div className="h-full bg-amber-400 w-1/3" data-id="element-750" />
                  <div className="h-full bg-green-500 w-1/3" data-id="element-751" />
                  <div className="absolute top-0 bottom-0 w-0.5 bg-gray-900 transition-all duration-300" style={{
                  left: `${score}%`
                }} data-id="element-752" />
                </div>
              </div>
              <div className="flex flex-col items-center gap-1.5 shrink-0" data-id="element-753">
                <span className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-full text-[10px] font-medium text-gray-600" data-id="element-754">
                  <MapPin className="w-3 h-3" data-id="element-755" /> Headquarters
                </span>
                <button className="w-7 h-7 rounded-lg bg-[#6C5CE7]/10 flex items-center justify-center hover:bg-[#6C5CE7]/20 transition-colors" data-id="element-756">
                  <MessageSquare className="w-3.5 h-3.5 text-[#6C5CE7]" data-id="element-757" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 border-b border-gray-200 justify-center" data-id="element-758">
              {['Overview', 'Notifications', 'Details', 'Financials', 'People', 'Applications'].map(tab => <button key={tab} className={`pb-2 text-[10px] font-medium uppercase tracking-wider relative transition-colors duration-300 ${tab === activeTab ? 'text-[#6C5CE7]' : 'text-gray-500'}`} data-id="element-759">
                  {tab}
                  {tab === activeTab && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 w-full h-0.5 bg-[#6C5CE7]" data-id="element-760" />}
                </button>)}
            </div>
          </div>

          <div className="flex-1 overflow-hidden p-5 bg-gray-50/30 relative" data-id="element-761">
            <AnimatePresence mode="wait" data-id="element-762">
              {activeTab === 'Overview' ? <motion.div key="overview" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden h-full flex flex-col" data-id="element-763">
                  {/* Score Summary */}
                  <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-4 shrink-0 relative" data-id="element-764">
                    <div className="absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300" style={{
                  backgroundColor: colors.stroke
                }} data-id="element-765" />
                    <div className="relative w-12 h-12 flex items-center justify-center shrink-0 ml-2" data-id="element-766">
                      <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-767">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="#f3f4f6" strokeWidth="8" data-id="element-768" />
                        <motion.circle cx="50" cy="50" r="45" fill="none" stroke={colors.stroke} strokeWidth="8" strokeDasharray="283" initial={{
                      strokeDashoffset: 283 - 283 * 68 / 100
                    }} animate={{
                      strokeDashoffset: 283 - 283 * score / 100
                    }} transition={{
                      duration: 0.3,
                      ease: 'easeOut'
                    }} data-id="element-769" />
                      </svg>
                      <span className="text-base font-bold text-gray-900" data-id="element-770">
                        {score}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-771">
                      <div className="flex items-center gap-2 mb-0.5" data-id="element-772">
                        <h3 className="text-sm font-bold text-gray-900" data-id="element-773">
                          Pulse Score
                        </h3>
                        <TrendingDown className="w-3.5 h-3.5 text-red-500" data-id="element-774" />
                        <span className={`text-[9px] font-bold ${colors.labelText} uppercase tracking-wider ${colors.labelBg} border ${colors.labelBorder} px-1.5 py-0.5 rounded transition-colors duration-300`} data-id="element-775">
                          {colors.label}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-500 leading-snug" data-id="element-776">
                        Stark Industries has declined{' '}
                        <strong className="text-red-600" data-id="element-777">-25 pts</strong> over 6
                        months. Hover over events on the chart to see what
                        happened.
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-gray-500 shrink-0" data-id="element-778">
                      <div className="w-2.5 h-0.5 bg-gray-900 rounded" data-id="element-779" /> Pulse
                      Score
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="flex-1 px-5 py-3 min-h-0" data-id="element-780">
                    <div className="relative w-full h-full" data-id="element-781">
                      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet" data-id="element-782">
                        {/* Grid lines */}
                        {[100, 80, 60, 40, 20, 0].map(val => {
                      const y = scoreToY(val);
                      return <g key={val} data-id="element-783">
                              <line x1={PAD_LEFT} y1={y} x2={VB_W - PAD_RIGHT} y2={y} stroke="#f3f4f6" strokeWidth="0.5" data-id="element-784" />
                              <text x="4" y={y + 3} fontSize="9" fill="#9ca3af" data-id="element-785">
                                {val}
                              </text>
                            </g>;
                    })}

                        {/* Green threshold line at 70 */}
                        <line x1={PAD_LEFT} y1={scoreToY(70)} x2={VB_W - PAD_RIGHT} y2={scoreToY(70)} stroke="#22c55e" strokeWidth="0.5" strokeDasharray="6,4" opacity="0.5" data-id="element-786" />
                        {/* Red threshold line at 40 */}
                        <line x1={PAD_LEFT} y1={scoreToY(40)} x2={VB_W - PAD_RIGHT} y2={scoreToY(40)} stroke="#ef4444" strokeWidth="0.5" strokeDasharray="6,4" opacity="0.5" data-id="element-787" />

                        {/* Gradient fill */}
                        <defs data-id="element-788">
                          <linearGradient id="declineGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-789">
                            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.08" data-id="element-790" />
                            <stop offset="100%" stopColor="#ef4444" stopOpacity="0" data-id="element-791" />
                          </linearGradient>
                        </defs>
                        <motion.path d={`${scoreLine()} L${toXY(CHART_DATA.length - 1).x},${VB_H - PAD_BOTTOM} L${toXY(0).x},${VB_H - PAD_BOTTOM} Z`} fill="url(#declineGrad)" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      duration: 1.5
                    }} data-id="element-792" />

                        {/* Score line */}
                        <motion.path d={scoreLine()} fill="none" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      duration: 0.8
                    }} data-id="element-793" />

                        {/* Data points */}
                        {CHART_DATA.map((point, i) => {
                      const {
                        x,
                        y
                      } = toXY(i);
                      const hasEvent = !!point.event;
                      const isHovered = hoveredPoint === i;
                      const color = dotColor(point.score);
                      return <g key={i} data-id="element-794">
                              {/* Glow effect for hovered dots */}
                              {isHovered && <motion.circle cx={x} cy={y} r={10} fill={color} opacity="0.15" initial={{
                          scale: 0
                        }} animate={{
                          scale: 1
                        }} transition={{
                          type: 'spring'
                        }} data-id="element-795" />}
                              <motion.circle cx={x} cy={y} r={hasEvent ? 5 : 3.5} fill={color} stroke="white" strokeWidth="1.5" initial={{
                          scale: 0
                        }} animate={{
                          scale: 1
                        }} transition={{
                          delay: 0.6 + i * 0.1,
                          type: 'spring'
                        }} data-id="element-796" />
                              {/* Dashed vertical line on hover */}
                              {isHovered && <line x1={x} y1={PAD_TOP} x2={x} y2={VB_H - PAD_BOTTOM} stroke="#6b7280" strokeWidth="0.5" strokeDasharray="3,3" opacity="0.3" data-id="element-797" />}
                            </g>;
                    })}

                        {/* Month labels */}
                        {CHART_DATA.map((point, i) => {
                      const {
                        x
                      } = toXY(i);
                      return <text key={i} x={x} y={VB_H - 6} textAnchor="middle" fontSize="9" fill="#9ca3af" data-id="element-798">
                              {point.month}
                            </text>;
                    })}
                      </svg>

                      {/* Tooltip overlay positioned relative to SVG */}
                      <AnimatePresence data-id="element-799">
                        {hoveredPoint !== null && CHART_DATA[hoveredPoint].event && <motion.div key={hoveredPoint} initial={{
                      opacity: 0,
                      y: 5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 5
                    }} transition={{
                      duration: 0.2
                    }} className="absolute z-10 pointer-events-none" style={{
                      left: `${toXY(hoveredPoint).x / VB_W * 100}%`,
                      top: `${toXY(hoveredPoint).y / VB_H * 100}%`,
                      transform: 'translate(-50%, -100%) translateY(-12px)'
                    }} data-id="element-800">
                              <div className="bg-white border border-gray-200 rounded-lg shadow-xl px-3 py-2.5 text-xs w-[200px] relative" data-id="element-801">
                                <div className="flex justify-between items-center mb-1.5" data-id="element-802">
                                  <span className="font-bold text-gray-900" data-id="element-803">
                                    {CHART_DATA[hoveredPoint].month}-25
                                  </span>
                                  <div className="flex items-center gap-1" data-id="element-804">
                                    <div className="w-2 h-2 rounded-full" style={{
                              backgroundColor: dotColor(CHART_DATA[hoveredPoint].score)
                            }} data-id="element-805" />
                                    <span className={`font-bold ${CHART_DATA[hoveredPoint].score <= 50 ? 'text-red-600' : 'text-amber-600'}`} data-id="element-806">
                                      Score: {CHART_DATA[hoveredPoint].score}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-[10px] text-gray-600 leading-snug mb-1.5" data-id="element-807">
                                  {CHART_DATA[hoveredPoint].event}
                                </p>
                                <p className="text-[9px] text-[#6C5CE7] italic" data-id="element-808">
                                  See the Notifications tab for details
                                </p>
                              </div>
                            </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div> : <motion.div key="notifications" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4
            }} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex flex-col" data-id="element-809">
                  <div className="px-5 py-4 border-b border-gray-100" data-id="element-810">
                    <h3 className="text-base font-bold text-gray-900" data-id="element-811">
                      Notifications
                    </h3>
                  </div>
                  <div className="overflow-x-auto" data-id="element-812">
                    <table className="w-full text-left border-collapse" data-id="element-813">
                      <thead data-id="element-814">
                        <tr className="border-b border-gray-100 text-[11px] text-gray-500 font-medium bg-gray-50/50" data-id="element-815">
                          <th className="px-5 py-3 w-10" data-id="element-816">
                            <div className="w-3.5 h-3.5 rounded border border-gray-300" data-id="element-817" />
                          </th>
                          <th className="px-5 py-3" data-id="element-818">Event</th>
                          <th className="px-5 py-3 w-28" data-id="element-819">Date</th>
                          <th className="px-5 py-3 w-24" data-id="element-820">Impact</th>
                          <th className="px-5 py-3 w-40" data-id="element-821">Acknowledged By</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-822">
                        {NOTIFICATIONS.map((notif, i) => <motion.tr key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: phase >= 4 ? 1 : 0,
                      x: phase >= 4 ? 0 : -10
                    }} transition={{
                      duration: 0.3,
                      delay: i * 0.1
                    }} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-id="element-823">
                            <td className="px-5 py-3.5" data-id="element-824">
                              <div className="w-3.5 h-3.5 rounded border border-gray-300" data-id="element-825" />
                            </td>
                            <td className="px-5 py-3.5 text-xs text-gray-900" data-id="element-826">
                              {notif.event}
                            </td>
                            <td className="px-5 py-3.5 text-xs text-gray-500" data-id="element-827">
                              {notif.date}
                            </td>
                            <td className="px-5 py-3.5" data-id="element-828">
                              <ImpactIcon type={notif.impact as 'red' | 'green'} data-id="element-829" />
                            </td>
                            <td className="px-5 py-3.5 text-xs text-gray-500" data-id="element-830">
                              {notif.ack}
                            </td>
                          </motion.tr>)}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-6 text-xs text-gray-500 bg-gray-50/30" data-id="element-831">
                    <span data-id="element-832">Rows per page: 5</span>
                    <span data-id="element-833">1-3 of 3</span>
                    <div className="flex gap-2" data-id="element-834">
                      <ChevronLeft className="w-4 h-4 text-gray-300" data-id="element-835" />
                      <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-836" />
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Scout AI Bot Popup */}
            <AnimatePresence data-id="element-837">
              {phase >= 5 && <motion.div initial={{
              opacity: 0,
              y: 50,
              scale: 0.9
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 20,
              scale: 0.9
            }} transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200
            }} className="absolute bottom-4 right-4 max-w-sm z-20 flex items-end gap-3" data-id="element-838">
                  <div className="bg-white rounded-2xl rounded-br-sm shadow-xl border border-gray-200 p-4 flex-1" data-id="element-839">
                    <div className="flex items-center gap-1.5 mb-2" data-id="element-840">
                      <span className="font-bold text-gray-900 text-xs" data-id="element-841">
                        Scout AI
                      </span>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#6C5CE7] animate-pulse" data-id="element-842" />
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed min-h-[60px]" data-id="element-843">
                      {scoutText}
                      <span className="inline-block w-1 h-3 bg-[#6C5CE7] ml-0.5 animate-pulse" data-id="element-844" />
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 shadow-lg border-2 border-white bg-white" data-id="element-845">
                    <img src="https://cdn.magicpatterns.com/uploads/5wLc6M5ofpVg64Brxe3VHb/image.png" alt="Scout AI" className="w-full h-full object-cover" data-id="element-846" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}